import { create } from 'zustand';
import api from '../lib/api';
import { NATIVE_ASSET_MAP, snapTransform } from '../components/game/environment/prefabs/nativeRegistry';

const ROT_STEP = Math.PI / 12; // 15 degrees
const SCALE_STEP = 0.1;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

/**
 * World Builder shared state (admin tooling + the saved-object cache everyone
 * renders). The DOM panel and the in-Canvas layer both read/write this store.
 */
export const useWorldBuilderStore = create((set, get) => ({
  builderEnabled: false,
  topTab: 'midgard', // 'midgard' | 'plugins'
  activeCategory: 'Nature',
  search: '',

  objects: [],
  loaded: false,

  selectedAssetId: null, // asset chosen for placement
  ghostPosition: [0, 0, 0],

  selectedObjectId: null, // placed object currently selected for editing
  draft: null, // { position:[x,y,z], rotation_y, scale }
  moveMode: false,

  toggleBuilder: () =>
    set((s) => ({
      builderEnabled: !s.builderEnabled,
      selectedAssetId: null,
      selectedObjectId: null,
      draft: null,
      moveMode: false,
    })),
  setCategory: (c) => set({ activeCategory: c }),
  setTopTab: (t) =>
    set({ topTab: t, activeCategory: t === 'midgard' ? 'Nature' : 'buildings', selectedAssetId: null }),
  setSearch: (q) => set({ search: q }),

  selectAsset: (id) =>
    set({ selectedAssetId: id, selectedObjectId: null, draft: null, moveMode: false }),
  clearAsset: () => set({ selectedAssetId: null }),
  setGhost: (pos) => set({ ghostPosition: pos }),

  fetchObjects: async () => {
    try {
      const res = await api.get('/world/objects');
      set({ objects: res.data.objects || [], loaded: true });
    } catch (e) {
      set({ loaded: true });
    }
  },

  placeAt: async (pos) => {
    const { selectedAssetId, activeCategory } = get();
    if (!selectedAssetId) return;
    const t = snapTransform(selectedAssetId, pos[0], pos[2]);
    try {
      const res = await api.post('/world/objects', {
        asset_id: selectedAssetId,
        category: activeCategory,
        position: t.position,
        rotation_y: 0,
        scale: 1,
      });
      set((s) => ({ objects: [...s.objects, res.data] }));
    } catch (e) {
      /* ignore */
    }
  },

  selectObject: (id) => {
    const obj = get().objects.find((o) => o.id === id);
    if (!obj) return;
    set({
      selectedObjectId: id,
      selectedAssetId: null,
      moveMode: false,
      draft: {
        position: [...obj.position],
        rotation_y: obj.rotation_y || 0,
        scale: obj.scale || 1,
      },
    });
  },

  toggleMoveMode: () => set((s) => ({ moveMode: !s.moveMode })),

  moveSelectedTo: (pos) =>
    set((s) => {
      if (!s.draft) return {};
      const obj = s.objects.find((o) => o.id === s.selectedObjectId);
      const t = snapTransform(obj ? obj.asset_id : null, pos[0], pos[2]);
      return { draft: { ...s.draft, position: t.position }, moveMode: false };
    }),

  rotateSelected: (dir) =>
    set((s) => {
      if (!s.draft) return {};
      const obj = s.objects.find((o) => o.id === s.selectedObjectId);
      const meta = obj ? NATIVE_ASSET_MAP[obj.asset_id] : null;
      const step = meta && meta.snap ? meta.snap.rotStep : ROT_STEP;
      return { draft: { ...s.draft, rotation_y: s.draft.rotation_y + dir * step } };
    }),

  scaleSelected: (dir) =>
    set((s) =>
      s.draft
        ? {
            draft: {
              ...s.draft,
              scale: clamp(+(s.draft.scale + dir * SCALE_STEP).toFixed(2), 0.1, 20),
            },
          }
        : {}
    ),

  saveSelected: async () => {
    const { selectedObjectId, draft } = get();
    if (!selectedObjectId || !draft) return;
    try {
      const res = await api.put(`/world/objects/${selectedObjectId}`, {
        position: draft.position,
        rotation_y: draft.rotation_y,
        scale: draft.scale,
      });
      set((s) => ({
        objects: s.objects.map((o) => (o.id === selectedObjectId ? res.data : o)),
        selectedObjectId: null,
        draft: null,
        moveMode: false,
      }));
    } catch (e) {
      /* ignore */
    }
  },

  cancelSelected: () => set({ selectedObjectId: null, draft: null, moveMode: false }),

  duplicateSelected: async () => {
    const { selectedObjectId, draft, objects } = get();
    const obj = objects.find((o) => o.id === selectedObjectId);
    if (!obj) return;
    const src = draft || {
      position: obj.position,
      rotation_y: obj.rotation_y || 0,
      scale: obj.scale || 1,
    };
    try {
      const res = await api.post('/world/objects', {
        asset_id: obj.asset_id,
        category: obj.category,
        position: [src.position[0] + 2, 0, src.position[2] + 2],
        rotation_y: src.rotation_y,
        scale: src.scale,
      });
      set((s) => ({
        objects: [...s.objects, res.data],
        selectedObjectId: res.data.id,
        draft: {
          position: [...res.data.position],
          rotation_y: res.data.rotation_y,
          scale: res.data.scale,
        },
        moveMode: false,
      }));
    } catch (e) {
      /* ignore */
    }
  },

  deleteSelected: async () => {
    const { selectedObjectId } = get();
    if (!selectedObjectId) return;
    try {
      await api.delete(`/world/objects/${selectedObjectId}`);
      set((s) => ({
        objects: s.objects.filter((o) => o.id !== selectedObjectId),
        selectedObjectId: null,
        draft: null,
        moveMode: false,
      }));
    } catch (e) {
      /* ignore */
    }
  },
}));
